import { useEffect, useState } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { ua } from 'zod/v4/locales';

import useAuth from './useAuth';

interface Options {
  label?: string;
  debug?: string;
}

export const useTapSentinel = <T extends HTMLElement | null>(
  ref: React.RefObject<T>,
  { label = 'tap', debug }: Options = {},
) => {
  const [fired, setFired] = useState(true);

  const markFired = () => {
    setFired(true);
  };

  const { data: auth } = useAuth();

  useEffect(() => {
    if (!ref.current) return;

    const el = ref.current;

    const onPointerUp = (e: PointerEvent) => {
      const { clientX: x, clientY: y } = e;
      const rect = el.getBoundingClientRect();
      const inside =
        x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
      if (!inside) return;

      // Start a timer; cancel it if markFired() runs
      window.setTimeout(() => {
        // If markFired was never called, report

        if (!fired) {
          const path = getPath(el);

          const userId = auth?.userId;

          const device = {
            hover: matchMedia('(hover: hover)').matches,
            pointer: matchMedia('(pointer: coarse)').matches
              ? 'coarse'
              : 'fine/mixed',
            touchPoints: navigator.maxTouchPoints,
            screen: {
              w: window.screen.width,
              h: window.screen.height,
              dpr: window.devicePixelRatio,
            },
            viewport: { w: window.innerWidth, h: window.innerHeight },
            pointerType: e.pointerType || null,
          };

          const elementRect: Pick<
            DOMRectReadOnly,
            'x' | 'y' | 'width' | 'height' | 'top' | 'right' | 'bottom' | 'left'
          > = {
            x: rect.x,
            y: rect.y,
            width: rect.width,
            height: rect.height,
            top: rect.top,
            right: rect.right,
            bottom: rect.bottom,
            left: rect.left,
          };

          const uaBrands = (
            navigator as {
              userAgentData?: {
                brands: { brand: string; version: string }[];
              };
            }
          ).userAgentData?.brands;

          const report = {
            type: 'MISSING_HANDLER_FIRE',
            label,
            coords: { x, y },
            elementRect,
            topElementPath: path,
            browser: ua,
            uaData: uaBrands?.length
              ? uaBrands
                  .map(
                    (b: { brand: string; version: string }) =>
                      `${b.brand} ${b.version}`,
                  )
                  .join(', ')
              : null,
            device,
            user: userId ? { id: userId } : null,
            timestamp: new Date().toISOString(),
          };

          datadogRum.addError(
            new Error(`Tap handler did not fire!! ${debug}`),
            { section: 'tapSentinel', ...report },
          );

          console.warn('[tapSentinel]', report);
        }
      }, 500);
    };

    document.addEventListener('pointerup', onPointerUp, true);
    return () => {
      document.removeEventListener('pointerup', onPointerUp, true);
    };
  }, [ref, label, debug, fired, auth?.userId]);

  return { markFired };
};

const getPath = (node: Element | null) => {
  const out: string[] = [];
  let n: Element | null = node;
  while (n && out.length < 10) {
    const id = n.id ? `#${n.id}` : '';
    const cls =
      n.className && typeof n.className === 'string'
        ? '.' + n.className.split(/\s+/).join('.')
        : '';
    out.push(`${n.tagName.toLowerCase()}${id}${cls}`);
    n = n.parentElement;
  }
  return out.join(' > ');
};
